import React from "react";
import { FaRobot, FaTasks, FaCalendarAlt, FaLinkedin, FaChartLine, FaCogs } from "react-icons/fa";
import { Footer } from "../components/shared/Footer";
import { Breadcrumb } from "../components/shared/Breadcrumb";

export default function Page() {
  const breadcrumbItems = [
    { name: 'Services', href: '/services' },
    { name: 'Workflow Solutions', href: '/workflow-solutions' }
  ];

  const capabilities = [
    {
      icon: FaTasks,
      title: "Project & Task Management",
      description: "Seamlessly integrates with tools like Asana, Trello, or Jira to manage tasks, track progress, and automate project workflows."
    },
    {
      icon: FaCalendarAlt,
      title: "Calendar & Email Integration",
      description: "Intelligent scheduling and email management through Google Workspace or Microsoft 365, including meeting coordination and follow-ups."
    },
    {
      icon: FaLinkedin,
      title: "Content Generation & Social Media",
      description: "Automated content creation for LinkedIn posts, blogs, and marketing materials, maintaining your brand voice and engagement."
    },
    {
      icon: FaChartLine,
      title: "Business Intelligence",
      description: "Connect to accounting software, generate reports, and provide real-time insights into business operations and metrics."
    },
    {
      icon: FaCogs,
      title: "Custom Workflow Design",
      description: "Tailored automation flows designed specifically for your business processes and integration needs."
    },
    {
      icon: FaRobot,
      title: "Managed AI Assistant",
      description: "Optional fully-managed hosting and maintenance of your AI assistant, ensuring optimal performance and reliability."
    }
  ];

  return (
    <div className="bg-gray-900 min-h-screen">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ 
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23FFFFFF\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          backgroundSize: '30px 30px'
        }} />
      </div>

      {/* Content container with top padding for fixed navbar */}
      <div className="pt-16">
        {/* Breadcrumb */}
        <div className="relative z-10">
          <div className="bg-gray-800/80 border-b border-gray-700">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative">
          {/* Hero Section */}
          <section className="py-16">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ 
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23FFFFFF\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                backgroundSize: '30px 30px'
              }} />
            </div>
            
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
                  Your Personal AI Operations Assistant
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-xl text-gray-300">
                  Imagine an AI assistant that doesn't just chat - it takes action. Using n8n's powerful workflow automation platform, we create sophisticated AI assistants that integrate deeply with your business tools and processes.
                </p>
              </div>
            </div>
          </section>

          {/* Capabilities Grid */}
          <section className="py-20">
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3">
                {capabilities.map((capability) => (
                  <div key={capability.title} className="relative overflow-hidden rounded-2xl bg-gray-800 p-8">
                    <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 opacity-20 blur-2xl" />
                    <div className="relative flex flex-col items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-500">
                        <capability.icon className="h-6 w-6 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">{capability.title}</h3>
                        <p className="mt-2 text-gray-300">{capability.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <section className="py-24">
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 p-8 md:p-12">
                <div className="text-center">
                  <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                    Ready to Transform Your Operations?
                  </h2>
                  <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-50">
                    Let's discuss how a custom AI assistant can streamline your business processes and boost productivity.
                  </p>
                  <div className="mt-8">
                    <a
                      href="/contact"
                      className="inline-flex items-center justify-center rounded-md bg-white px-6 py-3 text-base font-medium text-blue-600 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                    >
                      Schedule a Consultation
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </div>
    </div>
  );
}
